import { useState, useEffect, useMemo } from "react";
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Layers, Droplets, Gauge, Weight, CheckCircle2, AlertCircle,
  Info, ChevronRight, Save, RotateCcw, Microscope, CircleDot,
  TrendingUp, BarChart3, Zap, Thermometer, Timer,
  Factory, Milk, FishSymbol, ShieldAlert, ArrowDown, Waves, ArrowRightLeft,
  Settings, FlaskConical, Filter, Database, Beaker
} from "lucide-react";
import { useSampleLoader, fmt as sharedFmt } from '@/lib/equipmentSpecUtils';
import { SampleSelector } from '@/components/SampleSelector';
import PageHeader from '@/components/PageHeader';
import { ScienceAccordion } from '@/components/ScienceAccordion';

/* ─── Types ─────────────────────────────────────────────── */
interface MultimediaParams {
  flowRate: number;          // m³/day
  influentTSS: number;       // mg/L
  influentCOD: number;       // mg/L
  influentOil: number;       // mg/L
  targetTSS: number;         // mg/L effluent
  og: number; ph: number; alkalinity: number; turbidity: number;
  conductivity: number; hardness: number; nh3n: number; tp: number;

  // Vessel geometry
  vesselDiameter: number;    // m
  anthraciteDepth: number;   // m (top layer)
  sandDepth: number;         // m (middle)
  garnetDepth: number;       // m (bottom)
  freeboard: number;         // m (above media)
  numVessels: number;        // count

  // Media properties
  anthraciteES: number;     // mm (effective size d10)
  sandES: number;           // mm
  garnetES: number;         // mm
  anthraciteUC: number;     // uniformity coeff
  sandUC: number;
  garnetUC: number;

  // Process
  filtrationRate: number;   // m³/m²·h (approach velocity)
  temperature: number;      // °C
  runTimeTarget: number;     // h between backwashes
}

interface LayerData {
  name: string;
  depth: number;            // m
  es: number;               // mm
  uc: number;
  porosity: number;
  density: number;          // kg/m³
  headLoss: number;           // m
  expansionPct: number;     // % during backwash
  tssRemoval: number;         // % contribution
}

interface MultimediaSize {
  vesselArea: number;        // m²
  totalMediaDepth: number;   // m
  totalVolume: number;       // m³ (vessel)
  mediaVolume: number;       // m³ (media only)
  actualFiltrationRate: number; // m³/m²·h
  runTimeActual: number;     // h before head loss limit

  // Layer analysis
  layers: LayerData[];
  totalHeadLoss: number;     // m
  headLossBackwash: number;  // m (at backwash rate)

  // Backwash
  backwashRate: number;      // m³/m²·h
  backwashFlow: number;      // m³/h per vessel
  backwashVolume: number;    // m³ per cycle
  airScourRate: number;      // Nm³/m²·h

  // Status
  status: 'optimal' | 'acceptable' | 'undersized' | 'overloaded';
  statusDetail: string;

  // Risks
  breakthroughRisk: 'low' | 'moderate' | 'high';
  breakthroughReason: string;
  mediaMigrationRisk: 'low' | 'moderate' | 'high';

  // Extras for diagram / UI
  approachVelocity: number;  // m/h alias
  headLossClean: number;      // m alias
  headLossDirty: number;      // m alias
  expansionPercent: number;  // %
  runtimeToBackwash: number; // h alias
  backwashDuration: number;    // min
  removalEfficiency: number;  // % TSS removal
  power: number;             // kW (pump)
  gValue: number;            // s⁻¹ (backwash shear)
  vesselVolume: number;       // m³ alias
}

interface WastewaterProfile {
  name: string;
  description: string;
  icon: any;
  defaults: Partial<MultimediaParams>;
}

const fmt = (v: any, d = 1) => (typeof v === 'number' && !isNaN(v) ? v : 0).toFixed(d);

/* ─── Engineering Calc Engine ──────────────────────────── */
export function calcMultimedia(p: MultimediaParams): MultimediaSize {
  const bedDepth = p.anthraciteDepth + p.sandDepth + p.garnetDepth;
  const area = Math.PI * Math.pow(p.vesselDiameter / 2, 2);
  const mediaVolume = area * bedDepth;
  const totalVolume = area * (bedDepth + p.freeboard);

  // ─── Water properties ───
  const tempK = p.temperature + 273.15;
  const mu = 0.001 * Math.pow(10, 247.8 / tempK - 140); // Pa·s
  const rhoWater = 998 - 0.2 * (p.temperature - 20); // kg/m³

  // ─── Flow and loading ───
  const q = p.flowRate / 24; // m³/h
  const approachVelocity = q / area; // m/h
  const ebct = mediaVolume / q; // h empty bed contact time
  const ebctMin = ebct * 60; // min

  // ─── Head loss (Ergun equation for each layer) ───
  const voidFractions = [0.48, 0.40, 0.36];
  const particleSizes = [p.anthraciteES, p.sandES, p.garnetES]; // mm
  const layerDepths = [p.anthraciteDepth, p.sandDepth, p.garnetDepth]; // m
  const bulkDensities = [1100, 2650, 4100]; // kg/m³ typical
  const porosities = [0.48, 0.40, 0.36];

  let totalHeadLoss = 0;
  const layers: LayerData[] = [];

  for (let i = 0; i < 3; i++) {
    const d = particleSizes[i] / 1000; // m
    const eps = voidFractions[i];
    const L = layerDepths[i];
    const u = approachVelocity / 3600; // m/s
    const term1 = (150 * Math.pow(1 - eps, 2) * mu * u) / (Math.pow(eps, 3) * d * d);
    const term2 = (1.75 * (1 - eps) * rhoWater * u * u) / (Math.pow(eps, 3) * d);
    const hl = ((term1 + term2) / 9810) * L; // m
    totalHeadLoss += hl;

    // Richardson-Zaki expansion: ε_bw = ε^(1/4.5) approximation
    const vTerminal = Math.sqrt(4 * 9.81 * d * (bulkDensities[i] - rhoWater) / (3 * 0.44 * rhoWater)); // m/s
    const backwashVel = 25 / 3600; // m/s (typical 25 m/h for sand)
    const expansionPct = backwashVel > 0 ? ((backwashVel / vTerminal) * 100) : 0;

    layers.push({
      name: i === 0 ? 'Anthracite' : i === 1 ? 'Sand' : 'Garnet',
      depth: L,
      es: particleSizes[i],
      uc: [p.anthraciteUC, p.sandUC, p.garnetUC][i],
      porosity: porosities[i],
      density: bulkDensities[i],
      headLoss: hl,
      expansionPct: Math.min(50, expansionPct),
      tssRemoval: 0, // computed below
    });
  }

  // ─── Filtration efficiency ───
  const mediaEffFactor = Math.min(1.0, (bedDepth / 0.6) * 0.8);
  const tssRemoval = Math.min(0.95, mediaEffFactor * (0.50 + (p.influentTSS > 200 ? 0.15 : 0)));
  const codRemoval = Math.min(0.40, mediaEffFactor * 0.25);

  // ─── Particle capture (pore clogging) ───
  const solidsCaptured = (p.flowRate * p.influentTSS * tssRemoval) / 1000; // kg/day
  const cloggingRate = solidsCaptured / mediaVolume; // kg/m³·day
  const maxHeadLoss = 3.0; // m typical limit
  const runTimeToClean = maxHeadLoss > totalHeadLoss ? (maxHeadLoss - totalHeadLoss) / (cloggingRate * 0.5) : 0;

  // ─── Backwash physics ───
  const backwashRate = 25; // m³/m²·h (sand layer reference)
  const backwashFlow = area * backwashRate;
  const backwashVolume = totalVolume * 3; // 3 BV
  const backwashDuration = (backwashVolume / backwashFlow) * 60; // min
  const airScourRate = 60; // Nm³/m²·h typical

  // ─── Power and shear ───
  const power = (backwashFlow * backwashRate * 9.81) / (3600 * 1000 * 0.7); // kW (simplified pump power)
  const gValue = Math.sqrt(power * 1000 / (mu * totalVolume)); // s⁻¹ backwash shear

  // ─── Status ───
  let status: MultimediaSize['status']; let statusDetail: string;
  if (approachVelocity <= 12 && totalHeadLoss <= 2 && ebctMin >= 5 && tssRemoval >= 0.50) {
    status = 'optimal'; statusDetail = `Optimal. Surface loading ${approachVelocity.toFixed(1)} m/h, head loss ${totalHeadLoss.toFixed(2)} m, TSS removal ${(tssRemoval*100).toFixed(0)}%.`;
  } else if (approachVelocity <= 20 && totalHeadLoss <= 4 && ebctMin >= 3) {
    status = 'acceptable'; statusDetail = `Acceptable. Surface loading ${approachVelocity.toFixed(1)} m/h. Monitor head loss.`;
  } else if (approachVelocity > 30 || totalHeadLoss > 6 || ebctMin < 1) {
    status = 'overloaded'; statusDetail = `Overloaded. Surface loading ${approachVelocity.toFixed(1)} m/h or head loss ${totalHeadLoss.toFixed(2)} m too high. Increase diameter or reduce flow.`;
  } else if (mediaVolume < 0.5 || area < 0.3) {
    status = 'undersized'; statusDetail = `Undersized. Media volume ${mediaVolume.toFixed(1)} m³. Increase bed depth.`;
  } else {
    status = 'oversized'; statusDetail = `Oversized. Consider reducing for cost savings.`;
  }

  const breakthroughRisk: MultimediaSize['breakthroughRisk'] = tssRemoval < 0.4 ? 'high' : tssRemoval < 0.6 ? 'moderate' : 'low';
  const breakthroughReason = tssRemoval < 0.4 ? 'Low TSS capture. Check media depth or flow rate.' : 'Filtration within design limits.';
  const mediaMigrationRisk: MultimediaSize['mediaMigrationRisk'] = approachVelocity > 15 ? 'high' : approachVelocity > 12 ? 'moderate' : 'low';

  return {
    vesselArea: area,
    totalMediaDepth: bedDepth,
    totalVolume,
    mediaVolume,
    actualFiltrationRate: approachVelocity,
    runTimeActual: runTimeToClean * 24,
    layers,
    totalHeadLoss,
    headLossBackwash: totalHeadLoss * 2.5,
    backwashRate,
    backwashFlow,
    backwashVolume,
    airScourRate,
    status,
    statusDetail,
    breakthroughRisk,
    breakthroughReason,
    mediaMigrationRisk,
    // extras
    approachVelocity,
    headLossClean: totalHeadLoss,
    headLossDirty: totalHeadLoss + cloggingRate * p.runTimeTarget * 0.5,
    expansionPercent: layers[1]?.expansionPct ?? 0,
    runtimeToBackwash: runTimeToClean * 24,
    backwashDuration,
    removalEfficiency: tssRemoval * 100,
    power,
    gValue,
    vesselVolume: totalVolume,
  };
}

/* ─── Wastewater Profiles ─────────────────────────── */
const PROFILES: WastewaterProfile[] = [
  {
    name: 'General Industrial',
    description: 'Standard industrial pre-treatment before GAC.',
    icon: Factory,
    defaults: {
      flowRate: 500, influentTSS: 80, influentCOD: 300, influentOil: 30, targetTSS: 10,
      og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
      vesselDiameter: 2.5, anthraciteDepth: 0.5, sandDepth: 0.25, garnetDepth: 0.15, freeboard: 0.5, numVessels: 2,
      anthraciteES: 1.4, sandES: 0.55, garnetES: 0.3,
      anthraciteUC: 1.4, sandUC: 1.3, garnetUC: 1.5,
      filtrationRate: 10, temperature: 20, runTimeTarget: 24,
    },
  },
  {
    name: 'Dairy Effluent',
    description: 'High organic load, fat residue. Post-DAF.',
    icon: Milk,
    defaults: {
      flowRate: 400, influentTSS: 150, influentCOD: 500, influentOil: 80, targetTSS: 15,
      og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
      vesselDiameter: 2.0, anthraciteDepth: 0.6, sandDepth: 0.3, garnetDepth: 0.15, freeboard: 0.5, numVessels: 2,
      anthraciteES: 1.5, sandES: 0.6, garnetES: 0.35,
      anthraciteUC: 1.4, sandUC: 1.3, garnetUC: 1.5,
      filtrationRate: 8, temperature: 25, runTimeTarget: 18,
    },
  },
  {
    name: 'Fish Processing',
    description: 'High solids, organic particles. Needs robust capture.',
    icon: FishSymbol,
    defaults: {
      flowRate: 250, influentTSS: 200, influentCOD: 400, influentOil: 60, targetTSS: 15,
      og: 220, ph: 6.9, alkalinity: 220, turbidity: 480, conductivity: 1800, hardness: 180, nh3n: 35, tp: 8,
      vesselDiameter: 1.8, anthraciteDepth: 0.55, sandDepth: 0.3, garnetDepth: 0.15, freeboard: 0.5, numVessels: 2,
      anthraciteES: 1.6, sandES: 0.65, garnetES: 0.3,
      anthraciteUC: 1.5, sandUC: 1.4, garnetUC: 1.6,
      filtrationRate: 7, temperature: 15, runTimeTarget: 12,
    },
  },
];

/* ─── Slider Component ──────────────────────────────── */
function MMSlider({
  label, value, min, max, step, unit, icon: Icon, color = '#06b6d4',
  onChange, info,
}: {
  label: string; value: number; min: number; max: number; step: number;
  unit: string; icon: any; color?: string; onChange: (v: number) => void; info?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color }} />
          <span className="text-xs font-medium text-slate-300">{label}</span>
          {info && (
            <div className="group relative">
              <Info className="w-3 h-3 text-slate-600 cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-44 p-2 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 rb-card-sm">
                {info}
              </div>
            </div>
          )}
        </div>
        <span className="text-xs font-bold text-white tabular-nums">
          {step < 0.1 ? value.toFixed(2) : step < 1 ? value.toFixed(1) : value.toFixed(0)}
          <span className="text-[10px] text-slate-500 font-normal ml-0.5">{unit}</span>
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
        style={{ accentColor: color }}
      />
    </div>
  );
}

/* ─── Gauge Component ──────────────────────────────── */
function MMGauge({ value, max, label, unit, color }: { value: number; max: number; label: string; unit: string; color: string }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]">
        <span className="text-slate-500">{label}</span>
        <span className="font-bold" style={{ color }}>{value.toFixed(1)} {unit}</span>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.4, ease: 'easeOut' }} className="h-full rounded-full" style={{ backgroundColor: color }} />
      </div>
    </div>
  );
}

/* ─── Profile Card ────────────────────────────────── */
function ProfileCard({ profile, isActive, onClick }: { profile: WastewaterProfile; isActive: boolean; onClick: () => void }) {
  const Icon = profile.icon;
  return (
    <button onClick={onClick} className={`w-full text-left p-3 rounded-xl border transition-all ${isActive ? 'bg-amber-500/10 border-amber-500/30' : 'bg-rb-elevated border-slate-800 hover:border-slate-700'}`}>
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? 'bg-amber-500/20' : 'bg-slate-800'}`}>
          <Icon className={`w-4 h-4 ${isActive ? 'text-amber-400' : 'text-slate-500'}`} />
        </div>
        <div>
          <p className={`text-xs font-semibold ${isActive ? 'text-amber-400' : 'text-slate-300'}`}>{profile.name}</p>
          <p className="text-[10px] text-slate-500 leading-tight">{profile.description}</p>
        </div>
      </div>
    </button>
  );
}

/* ─── Alert Banner ────────────────────────────────── */
function AlertBanner({ level, title, message }: { level: 'low' | 'moderate' | 'high' | 'critical'; title: string; message: string }) {
  const styles: any = {
    low: { border: 'border-emerald-500/20', bg: 'bg-emerald-500/5', text: 'text-emerald-400', icon: CheckCircle2 },
    moderate: { border: 'border-amber-500/20', bg: 'bg-amber-500/5', text: 'text-amber-400', icon: AlertCircle },
    high: { border: 'border-orange-500/20', bg: 'bg-orange-500/5', text: 'text-orange-400', icon: ShieldAlert },
    critical: { border: 'border-rose-500/20', bg: 'bg-rose-500/5', text: 'text-rose-400', icon: ShieldAlert },
  };
  const s = styles[level] || styles.moderate;
  const Icon = s.icon;
  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className={`rounded-xl border p-3 ${s.bg} ${s.border}`}>
      <div className="flex items-center gap-2"><Icon className={`w-4 h-4 ${s.text}`} /><span className={`text-xs font-bold uppercase ${s.text}`}>{title}</span></div>
      <p className={`text-[10px] mt-1 leading-relaxed ${s.text} opacity-80`}>{message}</p>
    </motion.div>
  );
}

/* ─── Media Stack Visual ────────────────────────────── */
function MediaStackVisual({ layers, freeboard }: { layers: LayerData[]; freeboard: number }) {
  const totalDepth = layers.reduce((sum, l) => sum + l.depth, 0) + freeboard;
  const scale = 100 / totalDepth;
  const layerColors = ['#f59e0b', '#38bdf8', '#f43f5e'];
  return (
    <div className="p-3 rb-card-sm">
      <div className="relative w-12 mx-auto bg-slate-800 rounded-sm overflow-hidden" style={{ height: 160 }}>
        <div className="absolute top-0 left-0 w-full bg-sky-500/10" style={{ height: `${freeboard * scale * 1.6}px` }} />
        {layers.map((layer, i) => {
          const prevDepth = layers.slice(0, i).reduce((s, l) => s + l.depth, 0);
          return (
            <div key={layer.name} className="absolute left-0 w-full flex items-center justify-center"
              style={{ top: `${(freeboard + prevDepth) * scale * 1.6}px`, height: `${layer.depth * scale * 1.6}px`, backgroundColor: layerColors[i] + '30', borderTop: `1px solid ${layerColors[i]}60` }}>
              <span className="text-[10px] font-bold" style={{ color: layerColors[i] }}>{layer.name}</span>
            </div>
          );
        })}
      </div>
      <div className="text-center mt-1">
        <span className="text-[10px] text-slate-500">{layers[0].name} → {layers[1].name} → {layers[2].name}</span>
      </div>
    </div>
  );
}

/* ─── Enterprise Animated Flow Diagram ────────────────── */
function FlowDiagram({ params, size }: { params: MultimediaParams; size: MultimediaSize }) {
  const safe = {
    headLossClean: typeof size?.headLossClean === 'number' ? size.headLossClean : 0,
    headLossDirty: typeof size?.headLossDirty === 'number' ? size.headLossDirty : 0,
    headLossBackwash: typeof size?.headLossBackwash === 'number' ? size.headLossBackwash : 0,
    backwashFlow: typeof size?.backwashFlow === 'number' ? size.backwashFlow : 0,
    backwashRate: typeof size?.backwashRate === 'number' ? size.backwashRate : 0,
    airScourRate: typeof size?.airScourRate === 'number' ? size.airScourRate : 0,
    backwashDuration: typeof size?.backwashDuration === 'number' ? size.backwashDuration : 0,
    expansionPercent: typeof size?.expansionPercent === 'number' ? size.expansionPercent : 0,
    backwashVolume: typeof size?.backwashVolume === 'number' ? size.backwashVolume : 0,
    runtimeToBackwash: typeof size?.runtimeToBackwash === 'number' ? size.runtimeToBackwash : 0,
    approachVelocity: typeof size?.approachVelocity === 'number' ? size.approachVelocity : 0,
    vesselArea: typeof size?.vesselArea === 'number' ? size.vesselArea : 0,
    vesselVolume: typeof size?.vesselVolume === 'number' ? size.vesselVolume : 0,
    power: typeof size?.power === 'number' ? size.power : 0,
    gValue: typeof size?.gValue === 'number' ? size.gValue : 0,
    removalEfficiency: typeof size?.removalEfficiency === 'number' ? size.removalEfficiency : 0,
  };

  return (
    <div className="p-4 rb-card-sm">
      <p className="text-[10px] text-slate-500 mb-3 uppercase tracking-wider font-bold flex items-center gap-2">
        <ArrowDown className="w-3 h-3" /> Process Flow — Influent → Multimedia Filter(s) → Backwash → Effluent
      </p>
      <svg viewBox="0 0 900 380" className="w-full h-auto">
        <defs>
          <linearGradient id="waterGradM" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rb-active)" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0f2847" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="mediaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2d2d1e" stopOpacity="0.7" />
            <stop offset="30%" stopColor="#3d3d28" stopOpacity="0.8" />
            <stop offset="60%" stopColor="#4d4d38" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#5d5d48" stopOpacity="0.7" />
          </linearGradient>
        </defs>

        <text x="450" y="18" fill="#94a3b8" fontSize="11" textAnchor="middle" fontWeight="bold">MULTIMEDIA FILTER — FILTRATION TRAIN</text>

        {/* Influent */}
        <rect x="30" y="55" width="100" height="80" rx="6" fill="url(#waterGradM)" stroke="#38bdf8" strokeWidth="2" />
        <rect x="34" y="59" width="92" height="20" rx="3" fill="#38bdf8" opacity="0.15" />
        <text x="80" y="72" fill="#38bdf8" fontSize="9" textAnchor="middle" fontWeight="bold">Influent</text>
        <text x="80" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">{fmt(params.flowRate, 0)} m³/day</text>
        <text x="80" y="100" fill="#f59e0b" fontSize="8" textAnchor="middle">TSS {fmt(params.influentTSS, 0)} mg/L</text>
        <text x="80" y="112" fill="#f59e0b" fontSize="8" textAnchor="middle">COD {fmt(params.influentCOD, 0)} mg/L</text>
        <text x="80" y="124" fill="#94a3b8" fontSize="8" textAnchor="middle">Oil {fmt(params.influentOil, 0)} mg/L</text>

        <line x1="132" y1="95" x2="170" y2="95" stroke="#38bdf8" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="168,92 175,95 168,98" fill="#38bdf8" />

        {/* Feed Pump */}
        <g transform="translate(172, 78)">
          <circle cx="18" cy="14" r="14" fill="#0f172a" stroke="#06b6d4" strokeWidth="1.5" />
          <line x1="18" y1="6" x2="18" y2="22" stroke="#06b6d4" strokeWidth="1.5" />
          <line x1="10" y1="14" x2="26" y2="14" stroke="#06b6d4" strokeWidth="1.5" />
          <line x1="12" y1="8" x2="24" y2="20" stroke="#06b6d4" strokeWidth="1" />
          <line x1="12" y1="20" x2="24" y2="8" stroke="#06b6d4" strokeWidth="1" />
          <rect x="-6" y="10" width="12" height="8" rx="2" fill="var(--rb-active)" stroke="#06b6d4" strokeWidth="1" />
          <text x="18" y="38" fill="#06b6d4" fontSize="7" textAnchor="middle">Feed Pump</text>
        </g>

        <line x1="212" y1="95" x2="250" y2="95" stroke="#38bdf8" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="248,92 255,95 248,98" fill="#38bdf8" />

        {/* Filter Vessel 1 */}
        <rect x="255" y="40" width="120" height="110" rx="6" fill="url(#waterGradM)" stroke="#10b981" strokeWidth="2" />
        <rect x="259" y="55" width="112" height="25" rx="2" fill="#5d5d48" opacity="0.8" />
        <rect x="259" y="82" width="112" height="25" rx="2" fill="#4d4d38" opacity="0.8" />
        <rect x="259" y="109" width="112" height="25" rx="2" fill="#3d3d28" opacity="0.8" />
        <text x="315" y="68" fill="#f59e0b" fontSize="7" textAnchor="middle">Anthracite {fmt(params.anthraciteDepth, 1)}m</text>
        <text x="315" y="95" fill="#f59e0b" fontSize="7" textAnchor="middle">Sand {fmt(params.sandDepth, 1)}m</text>
        <text x="315" y="122" fill="#f59e0b" fontSize="7" textAnchor="middle">Garnet {fmt(params.garnetDepth, 1)}m</text>
        <text x="315" y="145" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Vessel 1</text>
        <text x="315" y="158" fill="#94a3b8" fontSize="8" textAnchor="middle">Ø{fmt(params.vesselDiameter, 1)}m | Av {fmt(safe.approachVelocity, 1)} m/h</text>

        <line x1="377" y1="95" x2="410" y2="95" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="408,92 415,95 408,98" fill="#10b981" />

        {/* Filter Vessel 2 */}
        {params.numVessels >= 2 && (
          <g>
            <rect x="417" y="40" width="120" height="110" rx="6" fill="url(#waterGradM)" stroke="#10b981" strokeWidth="2" />
            <rect x="421" y="55" width="112" height="25" rx="2" fill="#5d5d48" opacity="0.8" />
            <rect x="421" y="82" width="112" height="25" rx="2" fill="#4d4d38" opacity="0.8" />
            <rect x="421" y="109" width="112" height="25" rx="2" fill="#3d3d28" opacity="0.8" />
            <text x="477" y="145" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Vessel 2</text>
            <text x="477" y="158" fill="#94a3b8" fontSize="8" textAnchor="middle">Runtime {fmt(safe.runtimeToBackwash, 0)} h</text>
          </g>
        )}

        {/* Arrow to effluent */}
        <line x1={params.numVessels >= 2 ? 539 : 377} y1="95" x2="580" y2="95" stroke="#10b981" strokeWidth="2" strokeDasharray="6 4" />
        <polygon points="578,92 585,95 578,98" fill="#10b981" />

        {/* Effluent */}
        <rect x="587" y="55" width="100" height="80" rx="6" fill="url(#waterGradM)" stroke="#10b981" strokeWidth="2" />
        <rect x="591" y="59" width="92" height="20" rx="3" fill="#10b981" opacity="0.15" />
        <text x="637" y="72" fill="#10b981" fontSize="9" textAnchor="middle" fontWeight="bold">Effluent</text>
        <text x="637" y="88" fill="#94a3b8" fontSize="8" textAnchor="middle">TSS {fmt(params.targetTSS, 0)} mg/L</text>
        <text x="637" y="100" fill="#94a3b8" fontSize="8" textAnchor="middle">Δh {fmt(safe.headLossClean, 2)}m clean</text>
        <text x="637" y="112" fill="#f59e0b" fontSize="8" textAnchor="middle">Δh {fmt(safe.headLossDirty, 2)}m dirty</text>
        <text x="637" y="124" fill="#10b981" fontSize="8" textAnchor="middle">Removal {fmt(safe.removalEfficiency, 0)}%</text>

        {/* Backwash line */}
        <line x1="315" y1="148" x2="315" y2="220" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5 3" />
        <line x1="315" y1="220" x2="150" y2="220" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5 3" />
        <polygon points="152,217 145,220 152,223" fill="#f59e0b" />

        {/* Backwash tank */}
        <rect x="50" y="200" width="90" height="40" rx="4" fill="#0f172a" stroke="#f59e0b" strokeWidth="1.5" />
        <text x="95" y="218" fill="#f59e0b" fontSize="8" textAnchor="middle">Backwash</text>
        <text x="95" y="230" fill="#94a3b8" fontSize="7" textAnchor="middle">{fmt(safe.backwashRate, 0)} m³/m²·h | {fmt(safe.backwashDuration, 0)} min</text>

        {/* Head loss annotation */}
        <text x="315" y="172" fill="#f59e0b" fontSize="7" textAnchor="middle">Ergun: Δh/L = 150(1-ε)²μv/(ε³d²ρg) + 1.75(1-ε)ρv²/(ε³dρg)</text>

        {/* Legend */}
        <rect x="30" y="345" width="840" height="24" rx="4" fill="#0f172a" stroke="var(--rb-active)" strokeWidth="1" />
        <text x="45" y="359" fill="#38bdf8" fontSize="9">— Water Flow</text>
        <text x="145" y="359" fill="#06b6d4" fontSize="9">— Feed Pump</text>
        <text x="245" y="359" fill="#10b981" fontSize="9">— Filter Vessel</text>
        <text x="345" y="359" fill="#f59e0b" fontSize="9">— Backwash</text>
        <text x="445" y="359" fill="#94a3b8" fontSize="9">Ergun eq | Av {fmt(safe.approachVelocity, 1)} m/h | G {fmt(safe.gValue, 0)} s⁻¹ | Runtime {fmt(safe.runtimeToBackwash, 0)} h</text>
      </svg>
    </div>
  );
}

/* ─── Main Page ───────────────────────────────────── */
export default function MultimediaSpecPage() {
  const [params, setParams] = useState<MultimediaParams>({
    flowRate: 500, influentTSS: 80, influentCOD: 300, influentOil: 30, targetTSS: 10,
    og: 120, ph: 6.8, alkalinity: 180, turbidity: 380, conductivity: 2500, hardness: 250, nh3n: 45, tp: 12,
    vesselDiameter: 2.5, anthraciteDepth: 0.5, sandDepth: 0.25, garnetDepth: 0.15, freeboard: 0.5, numVessels: 2,
    anthraciteES: 1.4, sandES: 0.55, garnetES: 0.3,
    anthraciteUC: 1.4, sandUC: 1.3, garnetUC: 1.5,
    filtrationRate: 10, temperature: 20, runTimeTarget: 24,
  });

  const [activeProfile, setActiveProfile] = useState(0);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();
  const [showFlow, setShowFlow] = useState(true);
  const [activeTab, setActiveTab] = useState<'influent' | 'tank' | 'other'>('influent');

  const size = useMemo(() => calcMultimedia(params), [params]);
  const sampleLoader = useSampleLoader<MultimediaParams>(setParams);

  const update = (key: keyof MultimediaParams, val: number | string) => {
    setParams((p) => ({ ...p, [key]: val }));
    setSaved(false);
  };

  const loadProfile = (idx: number) => {
    const prof = PROFILES[idx];
    if (prof.defaults) {
      setParams((p) => ({ ...p, ...(prof.defaults as Partial<MultimediaParams>) }));
      setActiveProfile(idx);
      setSaved(false);
      toast.success(`Loaded ${prof.name} profile`);
    }
  };

  const handleSave = () => {
    localStorage.setItem('rb_mm_spec', JSON.stringify(params));
    setSaved(true);
    toast.success('Multimedia specification saved');
  };

  const handleReset = () => {
    loadProfile(0);
    setSaved(false);
    sampleLoader.clearSample();
  };

  useEffect(() => {
    const s = localStorage.getItem('rb_mm_spec');
    if (s) { try { setParams(JSON.parse(s)); } catch {} }
  }, []);

  // Science accordion sections
  const scienceSections = useMemo(() => {
    const q = params.flowRate / 24;
    const area = Math.PI * Math.pow(params.vesselDiameter / 2, 2);
    const approachVelocity = q / area;
    const bedDepth = params.anthraciteDepth + params.sandDepth + params.garnetDepth;
    const mediaVolume = area * bedDepth;
    const tempK = params.temperature + 273.15;
    const mu = 0.001 * Math.pow(10, 247.8 / tempK - 140);
    const rhoWater = 998 - 0.2 * (params.temperature - 20);
    const voidFractions = [0.48, 0.40, 0.36];
    const particleSizes = [params.anthraciteES, params.sandES, params.garnetES];
    const layerDepths = [params.anthraciteDepth, params.sandDepth, params.garnetDepth];
    const bulkDensities = [1100, 2650, 4100];
    const porosities = [0.48, 0.40, 0.36];
    const layerNames = ['Anthracite', 'Sand', 'Garnet'];
    const layerResults = [];
    let totalHeadLoss = 0;
    for (let i = 0; i < 3; i++) {
      const d = particleSizes[i] / 1000;
      const eps = voidFractions[i];
      const L = layerDepths[i];
      const u = approachVelocity / 3600;
      const term1 = (150 * Math.pow(1 - eps, 2) * mu * u) / (Math.pow(eps, 3) * d * d);
      const term2 = (1.75 * (1 - eps) * rhoWater * u * u) / (Math.pow(eps, 3) * d);
      const hl = ((term1 + term2) / 9810) * L;
      totalHeadLoss += hl;
      const vTerminal = Math.sqrt(4 * 9.81 * d * (bulkDensities[i] - rhoWater) / (3 * 0.44 * rhoWater));
      const backwashVel = 25 / 3600;
      const expansionPct = backwashVel > 0 ? ((backwashVel / vTerminal) * 100) : 0;
      layerResults.push({
        name: layerNames[i],
        es: particleSizes[i],
        porosity: porosities[i],
        depth: L,
        headLoss: hl,
        expansionPct: Math.min(50, expansionPct),
      });
    }
    const mediaEffFactor = Math.min(1.0, (bedDepth / 0.6) * 0.8);
    const tssRemoval = Math.min(0.95, mediaEffFactor * (0.50 + (params.influentTSS > 200 ? 0.15 : 0)));
    const codRemoval = Math.min(0.40, mediaEffFactor * 0.25);
    const solidsCaptured = (params.flowRate * params.influentTSS * tssRemoval) / 1000;
    const cloggingRate = solidsCaptured / mediaVolume;
    const backwashRate = 25;
    const backwashFlow = area * backwashRate;
    const totalVolume = area * (bedDepth + params.freeboard);
    const backwashVolume = totalVolume * 3;
    const backwashDuration = (backwashVolume / backwashFlow) * 60;
    const power = (backwashFlow * backwashRate * 9.81) / (3600 * 1000 * 0.7);
    const gValue = Math.sqrt(power * 1000 / (mu * totalVolume));
    return [
      {
        title: 'Ergun Head Loss — Layer by Layer',
        icon: Layers,
        equations: [
          { label: 'Flow (hourly)', formula: 'Q = flowRate / 24', substitution: `Q = ${sharedFmt(params.flowRate, 0)} / 24 = ${sharedFmt(q, 1)} m³/h`, result: sharedFmt(q, 1), status: 'pass', unit: 'm³/h' },
          { label: 'Vessel area', formula: 'A = π × (D/2)²', substitution: `A = π × (${params.vesselDiameter}/2)² = ${sharedFmt(area, 2)} m²`, result: sharedFmt(area, 2), status: 'pass', unit: 'm²' },
          { label: 'Approach velocity', formula: 'v = Q / A', substitution: `v = ${sharedFmt(q, 1)} / ${sharedFmt(area, 2)} = ${sharedFmt(approachVelocity, 1)} m/h`, result: sharedFmt(approachVelocity, 1), status: approachVelocity <= 12 ? 'pass' : approachVelocity <= 20 ? 'warn' : 'fail', unit: 'm/h' },
          { label: 'Bed depth', formula: 'L = anthracite + sand + garnet', substitution: `L = ${params.anthraciteDepth} + ${params.sandDepth} + ${params.garnetDepth} = ${sharedFmt(bedDepth, 2)} m`, result: sharedFmt(bedDepth, 2), status: 'pass', unit: 'm' },
          ...layerResults.map((l: any) => ({
            label: `${l.name} head loss`,
            formula: 'Δh = [(150(1-ε)²μv)/(ε³d²) + 1.75(1-ε)ρv²/(ε³d)] × L / 9810',
            substitution: `Δh = ${sharedFmt(l.headLoss, 4)} m`,
            result: sharedFmt(l.headLoss, 4),
            status: l.headLoss < 1.5 ? 'pass' : l.headLoss < 3 ? 'warn' : 'fail',
            unit: 'm',
          })),
          { label: 'Total head loss', formula: 'ΣΔh', substitution: `ΣΔh = ${sharedFmt(totalHeadLoss, 3)} m`, result: sharedFmt(totalHeadLoss, 3), status: totalHeadLoss <= 2 ? 'pass' : totalHeadLoss <= 4 ? 'warn' : 'fail', unit: 'm' },
        ],
      },
      {
        title: 'Filtration Efficiency',
        icon: Filter,
        equations: [
          { label: 'Media efficiency factor', formula: 'f = min(1.0, (L/0.6) × 0.8)', substitution: `f = min(1.0, (${sharedFmt(bedDepth, 2)}/0.6) × 0.8) = ${sharedFmt(mediaEffFactor, 2)}`, result: sharedFmt(mediaEffFactor, 2), status: 'pass', unit: '' },
          { label: 'TSS removal', formula: 'η = min(0.95, f × (0.50 + (TSS>200 ? 0.15 : 0)))', substitution: `η = min(0.95, ${sharedFmt(mediaEffFactor, 2)} × ${sharedFmt(0.50 + (params.influentTSS > 200 ? 0.15 : 0), 2)}) = ${sharedFmt(tssRemoval * 100, 0)}%`, result: sharedFmt(tssRemoval * 100, 0), status: tssRemoval >= 0.50 ? 'pass' : 'warn', unit: '%' },
          { label: 'COD removal', formula: 'η_COD = min(0.40, f × 0.25)', substitution: `η_COD = min(0.40, ${sharedFmt(mediaEffFactor, 2)} × 0.25) = ${sharedFmt(codRemoval * 100, 0)}%`, result: sharedFmt(codRemoval * 100, 0), status: 'pass', unit: '%' },
          { label: 'Solids captured', formula: 'm = flowRate × TSS × η / 1000', substitution: `m = ${sharedFmt(params.flowRate, 0)} × ${params.influentTSS} × ${sharedFmt(tssRemoval, 2)} / 1000 = ${sharedFmt(solidsCaptured, 1)} kg/d`, result: sharedFmt(solidsCaptured, 1), status: 'pass', unit: 'kg/d' },
          { label: 'Clogging rate', formula: 'C = m / mediaVolume', substitution: `C = ${sharedFmt(solidsCaptured, 1)} / ${sharedFmt(mediaVolume, 1)} = ${sharedFmt(cloggingRate, 2)} kg/m³·d`, result: sharedFmt(cloggingRate, 2), status: 'pass', unit: 'kg/m³·d' },
        ],
      },
      {
        title: 'Backwash Physics',
        icon: Droplets,
        equations: [
          { label: 'Backwash rate', formula: 'v_bw = 25 m³/m²·h', substitution: `v_bw = 25 m³/m²·h`, result: '25', status: 'pass', unit: 'm³/m²·h' },
          { label: 'Backwash flow', formula: 'Q_bw = A × v_bw', substitution: `Q_bw = ${sharedFmt(area, 2)} × 25 = ${sharedFmt(backwashFlow, 1)} m³/h`, result: sharedFmt(backwashFlow, 1), status: 'pass', unit: 'm³/h' },
          { label: 'Backwash volume', formula: 'V_bw = totalVolume × 3', substitution: `V_bw = ${sharedFmt(totalVolume, 1)} × 3 = ${sharedFmt(backwashVolume, 1)} m³`, result: sharedFmt(backwashVolume, 1), status: 'pass', unit: 'm³' },
          { label: 'Backwash duration', formula: 't = (V_bw / Q_bw) × 60', substitution: `t = (${sharedFmt(backwashVolume, 1)} / ${sharedFmt(backwashFlow, 1)}) × 60 = ${sharedFmt(backwashDuration, 0)} min`, result: sharedFmt(backwashDuration, 0), status: 'pass', unit: 'min' },
          { label: 'Pump power', formula: 'P = (Q_bw × v_bw × 9.81) / (3600 × 1000 × 0.7)', substitution: `P = (${sharedFmt(backwashFlow, 1)} × 25 × 9.81) / (3600 × 1000 × 0.7) = ${sharedFmt(power, 3)} kW`, result: sharedFmt(power, 3), status: 'pass', unit: 'kW' },
          { label: 'Shear rate (G)', formula: 'G = √(P × 1000 / (μ × V))', substitution: `G = √(${sharedFmt(power, 3)} × 1000 / (${sharedFmt(mu, 5)} × ${sharedFmt(totalVolume, 1)})) = ${sharedFmt(gValue, 0)} s⁻¹`, result: sharedFmt(gValue, 0), status: 'pass', unit: 's⁻¹' },
        ],
      },
    ];
  }, [params]);

  const statusColors: any = {
    optimal: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', icon: CheckCircle2 },
    acceptable: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', icon: Info },
    overloaded: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', icon: AlertCircle },
    undersized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: AlertCircle },
    oversized: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: Info },
  };

  const sc = statusColors[size.status];
  const StatusIcon = sc.icon;

  return (
    <div className="max-w-[1440px] mx-auto space-y-5">
      <PageHeader
        icon={Layers}
        iconColor="text-emerald-400"
        title="Multimedia Filter Specification"
        subtitle="Three-layer depth filtration — Ergun head loss, Yao capture, Richardson-Zaki backwash"
        actions={<div className="flex items-center gap-2">
          <button onClick={handleReset} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-slate-800/80 border border-slate-800/60 text-slate-300 text-xs font-bold hover:bg-slate-700 hover:border-slate-600 transition-all"><RotateCcw className="w-3.5 h-3.5" />Reset</button>
          <button onClick={handleSave} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs font-bold border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"><Save className="w-3.5 h-3.5" />{saved ? '✓ Saved' : 'Save'}</button>
          <button onClick={() => navigate('/equipment-validation?tab=multimedia')} className="flex items-center gap-2 px-3.5 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs font-bold border border-sky-500/20 hover:bg-sky-500/20 transition-all"><Microscope className="w-3.5 h-3.5" />Validation</button>
        </div>}
      />

      {/* Sample Selector */}
      <SampleSelector loader={sampleLoader} />

      {/* Loaded sample readout */}
      {sampleLoader.baseSampleParams && (
        <div className="bg-rb-surface rounded-xl border border-sky-500/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Database className="w-4 h-4 text-sky-400" />Loaded Sample: Base → Current</h3>
            <span className="text-xs text-slate-500">Dilution: {sampleLoader.dilutionFactor.toFixed(1)}×</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs">
            {[
              { label: 'Flow', value: `${sharedFmt(params.flowRate, 0)} m³/d`, base: `${sharedFmt(sampleLoader.baseSampleParams.flowRate || 0, 0)}` },
              { label: 'TSS', value: `${sharedFmt(params.influentTSS, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tss || 0, 0)}` },
              { label: 'O&G', value: `${sharedFmt(params.og, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.og || 0, 0)}` },
              { label: 'COD', value: `${sharedFmt(params.influentCOD, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.cod || 0, 0)}` },
              { label: 'pH', value: `${sharedFmt(params.ph, 1)}`, base: `${sharedFmt(sampleLoader.baseSampleParams.ph || 0, 1)}` },
              { label: 'Temp', value: `${sharedFmt(params.temperature, 0)}°C`, base: `${sharedFmt(sampleLoader.baseSampleParams.temp || 0, 0)}°C` },
              { label: 'Alk', value: `${sharedFmt(params.alkalinity, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.alkalinity || 0, 0)}` },
              { label: 'Turb', value: `${sharedFmt(params.turbidity, 0)} NTU`, base: `${sharedFmt(sampleLoader.baseSampleParams.turbidity || 0, 0)}` },
              { label: 'Cond', value: `${sharedFmt(params.conductivity, 0)} μS/cm`, base: `${sharedFmt(sampleLoader.baseSampleParams.conductivity || 0, 0)}` },
              { label: 'Hard', value: `${sharedFmt(params.hardness, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.hardness || 0, 0)}` },
              { label: 'NH₃-N', value: `${sharedFmt(params.nh3n, 0)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.nh3n || 0, 0)}` },
              { label: 'TP', value: `${sharedFmt(params.tp, 1)} mg/L`, base: `${sharedFmt(sampleLoader.baseSampleParams.tp || 0, 1)}` },
            ].map((item) => (
              <div key={item.label} className="p-2 rb-inner-card-sm">
                <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
                <p className="text-sm font-bold text-white">{item.value}</p>
                <p className="text-[10px] text-slate-600">Base: {item.base}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Layout: Flow Left + Content Right */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">

        {/* LEFT: Process Flow Panel */}
        <div className="xl:col-span-7 space-y-4">
          <div className="flex items-center justify-between px-4 py-2 rb-inner-card">
            <h3 className="text-xs font-semibold text-cyan-400 flex items-center gap-2">
              <ArrowRightLeft className="w-3.5 h-3.5" /> Process Flow Diagram
            </h3>
            <button onClick={() => setShowFlow(!showFlow)} className="text-[10px] px-2 py-1 rounded-md bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/20 transition-all">
              {showFlow ? 'Collapse' : 'Expand'}
            </button>
          </div>

          <div className="mb-2 text-center">
            <p className="text-[10px] font-bold tracking-widest text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)] uppercase">
              Reynolds &amp; Bauhm Smart Tool 2.0 — All Rights Reserved
            </p>
          </div>

          {showFlow && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}>
              <FlowDiagram params={params} size={size} />
            </motion.div>
          )}

          {/* Media Stack Visual */}
          <MediaStackVisual layers={size.layers} freeboard={params.freeboard} />
        </div>

        {/* RIGHT: Content Panel */}
        <div className="xl:col-span-5 space-y-5">

          {/* Profile Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PROFILES.map((prof, i) => (
              <ProfileCard key={prof.name} profile={prof} isActive={activeProfile === i} onClick={() => loadProfile(i)} />
            ))}
          </div>

          {/* Status Banner */}
          <div className={`rounded-xl border p-3 ${sc.bg} ${sc.border}`}>
            <div className="flex items-center gap-2">
              <StatusIcon className={`w-4 h-4 ${sc.text}`} />
              <span className={`text-xs font-bold uppercase ${sc.text}`}>{size.status}</span>
            </div>
            <p className={`text-[10px] mt-1 leading-relaxed ${sc.text} opacity-80`}>{size.statusDetail}</p>
          </div>

          {/* Risk Banners */}
          <div className="grid grid-cols-2 gap-3">
            <AlertBanner level={size.breakthroughRisk} title="Breakthrough Risk" message={size.breakthroughReason} />
            <AlertBanner level={size.mediaMigrationRisk} title="Media Migration" message={size.mediaMigrationRisk === 'high' ? 'Approach velocity >15 m/h risks media carryover.' : 'Approach velocity within safe range.'} />
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 p-1 rounded-xl bg-slate-800">
            <button onClick={() => setActiveTab('influent')} className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-all ${activeTab === 'influent' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>Influent</button>
            <button onClick={() => setActiveTab('tank')} className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-all ${activeTab === 'tank' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>Tank / Design</button>
            <button onClick={() => setActiveTab('other')} className={`flex-1 text-xs font-medium py-1.5 rounded-lg transition-all ${activeTab === 'other' ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-500 hover:text-slate-300'}`}>Other</button>
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
              {activeTab === 'influent' && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Influent Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <MMSlider label="Flow Rate" value={params.flowRate} min={10} max={2000} step={10} unit="m³/d" icon={Droplets} color="#38bdf8" onChange={(v) => update('flowRate', v)} info="Total daily flow to filter" />
                    <MMSlider label="Influent TSS" value={params.influentTSS} min={5} max={500} step={5} unit="mg/L" icon={Layers} color="#f59e0b" onChange={(v) => update('influentTSS', v)} />
                    <MMSlider label="Influent COD" value={params.influentCOD} min={50} max={2000} step={10} unit="mg/L" icon={Zap} color="#f43f5e" onChange={(v) => update('influentCOD', v)} />
                    <MMSlider label="Influent Oil" value={params.influentOil} min={0} max={200} step={5} unit="mg/L" icon={Waves} color="#d97706" onChange={(v) => update('influentOil', v)} />
                    <MMSlider label="Target TSS" value={params.targetTSS} min={1} max={50} step={1} unit="mg/L" icon={CheckCircle2} color="#10b981" onChange={(v) => update('targetTSS', v)} />
                    <MMSlider label="Temperature" value={params.temperature} min={5} max={40} step={1} unit="°C" icon={Thermometer} color="#06b6d4" onChange={(v) => update('temperature', v)} />
                    <MMSlider label="O&G" value={params.og} min={20} max={1000} step={10} unit="mg/L" icon={Droplets} color="#f59e0b" onChange={(v) => update('og', v)} />
                    <MMSlider label="Influent pH" value={params.ph} min={4} max={9.5} step={0.1} unit="" icon={FlaskConical} color="#a855f7" onChange={(v) => update('ph', v)} />
                    <MMSlider label="Alkalinity" value={params.alkalinity} min={10} max={500} step={5} unit="mg/L CaCO₃" icon={Beaker} color="#10b981" onChange={(v) => update('alkalinity', v)} />
                    <MMSlider label="Turbidity" value={params.turbidity} min={10} max={2000} step={10} unit="NTU" icon={Filter} color="#f59e0b" onChange={(v) => update('turbidity', v)} />
                    <MMSlider label="Conductivity" value={params.conductivity} min={100} max={10000} step={50} unit="μS/cm" icon={Zap} color="#fbbf24" onChange={(v) => update('conductivity', v)} />
                    <MMSlider label="Hardness" value={params.hardness} min={10} max={1000} step={10} unit="mg/L CaCO₃" icon={Droplets} color="#3b82f6" onChange={(v) => update('hardness', v)} />
                    <MMSlider label="NH₃-N" value={params.nh3n} min={1} max={200} step={1} unit="mg/L" icon={Beaker} color="#8b5cf6" onChange={(v) => update('nh3n', v)} />
                    <MMSlider label="Total Phosphorus" value={params.tp} min={0.5} max={50} step={0.5} unit="mg/L" icon={FlaskConical} color="#ec4899" onChange={(v) => update('tp', v)} />
                  </div>
                </div>
              )}
              {activeTab === 'tank' && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Vessel & Media Design</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <MMSlider label="Vessel Diameter" value={params.vesselDiameter} min={0.5} max={5} step={0.1} unit="m" icon={Gauge} color="#10b981" onChange={(v) => update('vesselDiameter', v)} />
                    <MMSlider label="Anthracite Depth" value={params.anthraciteDepth} min={0.1} max={1.5} step={0.05} unit="m" icon={Layers} color="#f59e0b" onChange={(v) => update('anthraciteDepth', v)} />
                    <MMSlider label="Sand Depth" value={params.sandDepth} min={0.1} max={1.0} step={0.05} unit="m" icon={Layers} color="#38bdf8" onChange={(v) => update('sandDepth', v)} />
                    <MMSlider label="Garnet Depth" value={params.garnetDepth} min={0.05} max={0.5} step={0.05} unit="m" icon={Layers} color="#f43f5e" onChange={(v) => update('garnetDepth', v)} />
                    <MMSlider label="Freeboard" value={params.freeboard} min={0.2} max={1.5} step={0.1} unit="m" icon={ArrowDown} color="#94a3b8" onChange={(v) => update('freeboard', v)} />
                    <MMSlider label="Num Vessels" value={params.numVessels} min={1} max={6} step={1} unit="" icon={Settings} color="#64748b" onChange={(v) => update('numVessels', v)} />
                  </div>
                </div>
              )}
              {activeTab === 'other' && (
                <div className="space-y-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Media Properties & Process</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <MMSlider label="Anthracite ES" value={params.anthraciteES} min={0.8} max={2.0} step={0.1} unit="mm" icon={Microscope} color="#f59e0b" onChange={(v) => update('anthraciteES', v)} info="Effective size d10" />
                    <MMSlider label="Sand ES" value={params.sandES} min={0.3} max={1.0} step={0.05} unit="mm" icon={Microscope} color="#38bdf8" onChange={(v) => update('sandES', v)} />
                    <MMSlider label="Garnet ES" value={params.garnetES} min={0.2} max={0.6} step={0.05} unit="mm" icon={Microscope} color="#f43f5e" onChange={(v) => update('garnetES', v)} />
                    <MMSlider label="Anthracite UC" value={params.anthraciteUC} min={1.1} max={2.0} step={0.1} unit="" icon={BarChart3} color="#f59e0b" onChange={(v) => update('anthraciteUC', v)} info="d60/d10 uniformity" />
                    <MMSlider label="Sand UC" value={params.sandUC} min={1.1} max={2.0} step={0.1} unit="" icon={BarChart3} color="#38bdf8" onChange={(v) => update('sandUC', v)} />
                    <MMSlider label="Garnet UC" value={params.garnetUC} min={1.1} max={2.0} step={0.1} unit="" icon={BarChart3} color="#f43f5e" onChange={(v) => update('garnetUC', v)} />
                    <MMSlider label="Filtration Rate" value={params.filtrationRate} min={2} max={25} step={1} unit="m³/m²·h" icon={TrendingUp} color="#06b6d4" onChange={(v) => update('filtrationRate', v)} info="Design approach velocity" />
                    <MMSlider label="Run Time Target" value={params.runTimeTarget} min={4} max={72} step={1} unit="h" icon={Timer} color="#8b5cf6" onChange={(v) => update('runTimeTarget', v)} />
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Performance Gauges */}
          <div className="p-4 space-y-3 rb-inner-card">
            <h3 className="text-xs font-semibold text-slate-200 flex items-center gap-2"><Gauge className="w-3.5 h-3.5 text-sky-400" /> Performance Gauges</h3>
            <div className="grid grid-cols-2 gap-3">
              <MMGauge value={size.actualFiltrationRate} max={20} label="Approach Velocity" unit="m/h" color="#06b6d4" />
              <MMGauge value={size.totalHeadLoss} max={5} label="Head Loss" unit="m" color="#f59e0b" />
              <MMGauge value={size.runTimeActual} max={48} label="Runtime" unit="h" color="#10b981" />
              <MMGauge value={size.removalEfficiency} max={100} label="TSS Removal" unit="%" color="#8b5cf6" />
            </div>
          </div>

          {/* Sizing Panel */}
          <div className="p-4 space-y-2 rb-inner-card">
            <h3 className="text-xs font-semibold text-slate-200 flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5 text-emerald-400" /> Sizing & Performance</h3>
            <div className="space-y-1.5 text-[10px]">
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Vessel Area</span><span className="text-rb-brand font-bold">{fmt(size.vesselArea, 1)} m²</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Total Media Depth</span><span className="text-rb-brand font-bold">{fmt(size.totalMediaDepth, 2)} m</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Total Volume</span><span className="text-rb-brand font-bold">{fmt(size.totalVolume, 1)} m³</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Media Volume</span><span className="text-rb-brand font-bold">{fmt(size.mediaVolume, 1)} m³</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Actual Filtration Rate</span><span className="text-rb-brand font-bold">{fmt(size.actualFiltrationRate, 1)} m³/m²·h</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Runtime Actual</span><span className="text-rb-brand font-bold">{fmt(size.runTimeActual, 1)} h</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Head Loss Clean</span><span className="text-rb-brand font-bold">{fmt(size.totalHeadLoss, 2)} m</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Head Loss Backwash</span><span className="text-rb-brand font-bold">{fmt(size.headLossBackwash, 2)} m</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Backwash Rate</span><span className="text-rb-brand font-bold">{fmt(size.backwashRate, 0)} m³/m²·h</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Backwash Flow</span><span className="text-rb-brand font-bold">{fmt(size.backwashFlow, 1)} m³/h</span></div>
              <div className="flex justify-between p-2 rounded-lg bg-rb-surface"><span className="text-slate-500">Backwash Volume</span><span className="text-rb-brand font-bold">{fmt(size.backwashVolume, 1)} m³</span></div>
            </div>
          </div>

          {/* Layer Analysis */}
          <div className="p-4 space-y-2 rb-inner-card">
            <h3 className="text-xs font-semibold text-slate-200 flex items-center gap-2"><Layers className="w-3.5 h-3.5 text-amber-400" /> Layer Analysis (Ergun)</h3>
            <div className="space-y-1.5 text-[10px]">
              {size.layers.map((layer, i) => (
                <div key={layer.name} className="flex justify-between p-2 rounded-lg bg-rb-surface">
                  <span className="text-slate-500">{layer.name} (d10={fmt(layer.es, 1)}mm, ε={fmt(layer.porosity, 2)})</span>
                  <span className="text-rb-brand font-bold">Δh={fmt(layer.headLoss, 3)}m | Exp={fmt(layer.expansionPct, 0)}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Design Notes */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-amber-400" />Design Notes &amp; Warnings</h3>
            <div className="space-y-2 text-xs">
              {size.totalHeadLoss > 2.5 && <div className="p-2 rounded-lg bg-rose-500/5 border border-rose-500/20 text-rose-400">⚠️ Head loss {fmt(size.totalHeadLoss, 2)} m exceeds 2.5 m. Consider larger diameter or coarser media.</div>}
              {size.actualFiltrationRate > 15 && <div className="p-2 rounded-lg bg-rose-500/5 border border-rose-500/20 text-rose-400">⚠️ Approach velocity {fmt(size.actualFiltrationRate, 1)} m/h exceeds 15 m/h. Risk of media migration.</div>}
              {size.runTimeActual < 8 && <div className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-400">⚠️ Runtime {fmt(size.runTimeActual, 1)} h is very short. Check influent solids or increase bed depth.</div>}
              {size.removalEfficiency < 50 && <div className="p-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-400">ℹ️ TSS removal {fmt(size.removalEfficiency, 0)}% is below typical 50–65% for multimedia.</div>}
              <div className="p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-emerald-400">ℹ️ Backwash: {fmt(size.backwashRate, 0)} m³/m²·h for {fmt(size.backwashDuration, 0)} min. Air scour at {fmt(size.airScourRate, 0)} Nm³/m²·h recommended.</div>
            </div>
          </div>

          {/* Science Validation Accordion */}
          <div className="p-4 space-y-3 rb-card">
            <h3 className="text-sm font-bold text-white flex items-center gap-2"><Microscope className="w-4 h-4 text-sky-400" />Science Validation</h3>
            <ScienceAccordion sections={scienceSections} />
          </div>
        </div>
      </div>
    </div>
  );
}
